import React, { useEffect } from 'react';
import { RiCloseLine, RiFlightTakeoffLine, RiFlightLandLine, RiTimeLine, RiTeamLine } from '@remixicon/react';
import { cn } from '../../../../components/ui/utils';
import LocationInput from './LocationInput';
import DatePicker from './DatePicker';
import PassengerCountInput from './PassengerCountInput';

/**
 * FlightForm Component
 * Form for a single flight leg with departure, arrival, dates, passengers, and notes
 *
 * @param {Object} props
 * @param {Object} props.data - Flight leg data
 * @param {Function} props.onChange - Callback when data changes
 * @param {Object} props.errors - Validation errors for fields
 * @param {boolean} props.showReturnDate - Whether to show return date field
 * @param {number} props.legNumber - Leg number for multi-leg flights (null for single)
 * @param {Function} props.onRemove - Callback to remove this leg
 * @param {boolean} props.isRemovable - Whether this leg can be removed
 */
const FlightForm = ({
  data = {
    departure: null,
    arrival: null,
    departureDate: '',
    departureTime: '',
    returnDate: '',
    returnTime: '',
    passengerCount: 1,
    notes: ''
  },
  onChange,
  errors = {},
  showReturnDate = false,
  legNumber = null,
  onRemove = null,
  isRemovable = false,
  selectedBid = {}
}) => {
  
  // Handle field updates
  const handleFieldChange = (field, value) => {
    const updatedData = {
      ...data,
      [field]: value
    };
    onChange?.(updatedData);
  };

  // Get today's date for min date validation
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Get current time in HH:MM format for default value
  const getCurrentTime = () => {
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  // Initialize departureTime and returnTime with current time if not set
  useEffect(() => {
    const updates = {};
    if (!data?.departureTime) updates.departureTime = getCurrentTime();
    if (showReturnDate && !data?.returnTime) updates.returnTime = getCurrentTime();
    if (Object.keys(updates).length > 0 && onChange) {
      onChange({ ...data, ...updates });
    }
  }, [showReturnDate]);

  // Parse departure date for return date min validation
  const departureMinDate = data?.departureDate
    ? new Date(data.departureDate)
    : today;

  return (
    <div role="form" className="space-y-4">
      {/* Leg Header (for multi-leg) */}
      {legNumber !== null && (
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold text-gray-700">
            Leg {legNumber}
          </h4>
          {isRemovable && (
            <button
              type="button"
              onClick={onRemove}
              aria-label={`Remove leg ${legNumber}`}
              className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              <RiCloseLine className="w-5 h-5" />
            </button>
          )}
        </div>
      )}

      {/* Location Inputs Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Departure */}
        <div>
          <LocationInput
            value={data?.departure}
            onChange={(value) => handleFieldChange('departure', value)}
            placeholder="Search departure airport"
            error={!selectedBid?.departure_airport && errors?.departure || ''}
            label="Departure *"
            icon={RiFlightTakeoffLine}
          />
        </div>

        {/* Arrival */}
        <div>
          <LocationInput
            value={data?.arrival}
            onChange={(value) => handleFieldChange('arrival', value)}
            placeholder="Search arrival airport"
            error={!selectedBid?.arrival_airport && errors?.arrival || ''}
            label="Arrival *"
            icon={RiFlightLandLine}
          />
        </div>
      </div>

      {/* Date Inputs and Passenger Count Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Departure Date */}
        <div>
          <DatePicker
            value={data?.departureDate}
            onChange={(value) => handleFieldChange('departureDate', value)}
            minDate={today}
            placeholder="Select departure date"
            error={!selectedBid?.departure_date && errors?.departureDate || ''}
            label="Departure Date *"
          />
        </div>

        {/* Departure Time */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Departure Time *
          </label>
          <div className="relative">
            <RiTimeLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-700" />
            <input
              type="text"
              inputMode="numeric"
              placeholder="HH:MM"
              maxLength={5}
              value={data?.departureTime || getCurrentTime()}
              onChange={(e) => {
                let v = e.target.value.replace(/[^0-9:]/g, '');
                // Auto-insert colon after 2 digits
                if (v.length === 2 && !v.includes(':')) v += ':';
                if (v.length > 5) v = v.slice(0, 5);
                handleFieldChange('departureTime', v);
              }}
              onBlur={(e) => {
                const match = e.target.value.match(/^(\d{1,2}):(\d{2})$/);
                if (match) {
                  const h = Math.min(parseInt(match[1], 10), 23);
                  const m = Math.min(parseInt(match[2], 10), 59);
                  handleFieldChange('departureTime', `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
                }
              }}
              className={cn(
                'w-full pl-9 pr-4 py-2.5 border rounded-lg text-sm transition-all text-gray-700',
                'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
                errors?.departureTime ? 'border-red-500' : 'border-gray-300',
              )}
            />
          </div>
          {errors?.departureTime && (
            <p className="mt-1 text-xs text-red-600">{errors.departureTime}</p>
          )}
        </div>

        {/* Return Date (conditional) */}
        {showReturnDate && (
          <>
            <div>
              <DatePicker
                value={data?.returnDate || ''}
                onChange={(value) => handleFieldChange('returnDate', value)}
                minDate={departureMinDate}
                placeholder="Select return date"
                error={errors?.returnDate || ''}
                label="Return Date *"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Return Time *
              </label>
              <input
                type="text"
                inputMode="numeric"
                placeholder="HH:MM"
                maxLength={5}
                value={data?.returnTime || getCurrentTime()}
                onChange={(e) => {
                  let v = e.target.value.replace(/[^0-9:]/g, '');
                  if (v.length === 2 && !v.includes(':')) v += ':';
                  if (v.length > 5) v = v.slice(0, 5);
                  handleFieldChange('returnTime', v);
                }}
                onBlur={(e) => {
                  const match = e.target.value.match(/^(\d{1,2}):(\d{2})$/);
                  if (match) {
                    const h = Math.min(parseInt(match[1], 10), 23);
                    const m = Math.min(parseInt(match[2], 10), 59);
                    handleFieldChange('returnTime', `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
                  }
                }}
                className={cn(
                  'w-full px-4 py-2.5 border rounded-lg text-sm transition-all',
                  'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
                  errors?.returnTime ? 'border-red-500' : 'border-gray-300',
                )}
              />
              {errors?.returnTime && (
                <p className="mt-1 text-xs text-red-600">{errors.returnTime}</p>
              )}
            </div>
          </>
        )}

        {/* Passenger Count */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Passenger Count *
          </label>
          <div className="relative">
            <RiTeamLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-700" />
            <input
              type="text"
              value={data?.passengerCount ?? selectedBid?.passenger_count ?? 1}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9]/g, '');
                const numValue = value === '' ? 0 : parseInt(value, 10);
                handleFieldChange('passengerCount', numValue);
              }}
              min={0}
              max={50}
              className={cn(
                'w-full pl-9 pr-4 py-2.5 border rounded-lg text-sm transition-all text-gray-700',
                'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
                errors?.passengerCount ? 'border-red-500' : 'border-gray-300',
              )}
            />
          </div>
          {errors?.passengerCount && (
            <p className="mt-1 text-xs text-red-600">{errors.passengerCount}</p>
          )}
        </div>
      </div>

      {/* Notes */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Notes
        </label>
        <textarea
          value={data?.notes || ''}
          onChange={(e) => handleFieldChange('notes', e.target.value)}
          placeholder="Add any optional notes or special requirements"
          rows={3}
          className={cn(
            'w-full px-4 py-2.5 border rounded-lg text-sm transition-all resize-none',
            'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
            'border-gray-300 hover:border-gray-400'
          )}
        />
      </div>
    </div>
  );
};

export default FlightForm;